import { useMemo, useState } from 'react'
import { ScoopButton, ScoopDialog, ScoopText, ScoopTextField } from '../../../../ui'
import styles from './GroupRunsPanel.module.scss'

/**
 * Author a NEW run configuration (InvestigationGroupConfig JSON) and launch it —
 * the same object the CLI runs. v1 is a validated JSON editor, not a form: it
 * makes every customer launchable from the UI immediately; a structured form is
 * a later nicety. The backend re-validates on submit (runGroup parses the config
 * and rejects bad ones), so client-side checks here are a convenience layer.
 */
export const NewRunConfigDialog = ({ open, initialConfig, onClose, onLaunch, isLaunching }) => {
    const [configText, setConfigText] = useState('')
    const [touched, setTouched] = useState(false)

    // Seed from an existing group's config ("start from…") when provided.
    const effectiveText = touched ? configText : initialConfig || configText

    const parseIssue = useMemo(() => {
        if (!effectiveText.trim()) return 'Configuration is empty'
        try {
            const parsed = JSON.parse(effectiveText)
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                return 'Configuration must be a JSON object'
            }
            if (!parsed.group_name) return 'Missing "group_name"'
            return null
        } catch (e) {
            return `Not valid JSON: ${e.message}`
        }
    }, [effectiveText])

    const handleClose = () => {
        setConfigText('')
        setTouched(false)
        onClose()
    }

    return (
        <ScoopDialog
            open={open}
            onClose={handleClose}
            title="New run configuration"
            maxWidth="md"
            actions={
                <>
                    <ScoopButton variant="grey" onClick={handleClose}>
                        Cancel
                    </ScoopButton>
                    <ScoopButton
                        variant="default"
                        onClick={() => onLaunch(effectiveText)}
                        disabled={Boolean(parseIssue) || isLaunching}
                        loading={isLaunching}
                    >
                        Launch Run
                    </ScoopButton>
                </>
            }
        >
            <ScoopText className={styles.cautionText}>
                Paste or edit an investigation group configuration (the same JSON the CLI
                runs). The backend validates it fully on launch; launching starts a real
                investigation cycle.
            </ScoopText>
            <ScoopTextField
                value={effectiveText}
                onChange={(e) => {
                    setTouched(true)
                    setConfigText(e.target.value)
                }}
                placeholder='{"group_name": "…", "screening_contexts": ["…"], "entity_hierarchy": {…}}'
                multiline
                rows={18}
                className={styles.configEditor}
            />
            {parseIssue && effectiveText.trim() !== '' && (
                <ScoopText className={styles.error}>{parseIssue}</ScoopText>
            )}
        </ScoopDialog>
    )
}
